import { CloseButton } from "@/components/ui/close-button";
import { SIDE_NAV_QUERY, useAutoFocus, usePhone } from "@/hooks/use-media-query";
import { SystemUpgradeIcon, WorksheetIcon } from "@/lib/icons";
import { useProjectCurrencySync, useProjectTimeZoneSync } from "@/hooks/useProjectCountry";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { NotificationBadge } from "@/components/ui/notification-badge";
import { cn } from "@/lib/utils";
import { relativeTime } from "@/lib/relativeTime";
import { ReactNode, useCallback, useState, useEffect, useLayoutEffect, useRef, useMemo, lazy, Suspense, Fragment } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { useUIContext } from "@/contexts/UIContext";
import { EngagementBadge } from "@/components/EngagementBadge";
import { ModeAwareSidebar } from "@/components/ModeAwareSidebar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { isAdminPath } from "@/lib/platformLayer";
import { SkipNavigation } from "@/components/SkipNavigation";
import { UserAccountMenu } from "@/components/UserAccountMenu";
import { FeedbackButton } from "@/components/FeedbackButton";
import { LetterAvatar } from "@/components/LetterAvatar";
import { OPEN_ACCOUNT_EVENT } from "@/components/SidebarFooter";
import type { AccountTab } from "@/components/AccountSettingsDialog";

const ACCOUNT_TABS: readonly AccountTab[] = ["profile", "security", "notifications", "email", "appearance"];
/*
  LAZY, BECAUSE THIS SHELL WRAPS EVERY AUTHENTICATED ROUTE.

  Anything imported here lands in the entry chunk, which is what every user
  downloads before seeing anything. Measured from the build's own source map on
  2026-09-11, these two were the largest contributors in it: the contact modal
  203 KB of source and the intelligence chat another 46 KB, inside a 492 KB
  first load. Thirty-eight routes are already lazy; these were reachable only
  through the shell, so they never were.

  The chat is the sharper case — it renders only for a project holding an active
  Intelligence subscription, so every customer who has not bought the add-on was
  downloading it anyway, on every device, on every first load.

  Both were already rendered conditionally, so there is nothing to show while a
  chunk arrives and `fallback={null}` is honest rather than a placeholder for
  something the reader did not ask for.
*/
const ContactDetailModal = lazy(() =>
  import("@/components/ContactDetailModal").then((m) => ({ default: m.ContactDetailModal })),
);

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge-base";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertTriangle, ArrowRight, Bell, Mic, Search, Calendar, CheckCheck, CheckSquare, Clock, CreditCard, Inbox, Mails, Plus, Settings2, Trash2, TrendingUp, Trophy, CircleDollarSign, UserPlus, UserSearch, X } from "lucide-react";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { useIntelligence } from "@/contexts/IntelligenceContext";
import { useDebounce } from "@/hooks/useDebounce";
import { useTheme } from "@/components/ThemeProvider";
import { DynamicIcon } from "@/components/DynamicIcon";
/* The in-place record dialogs (openRecord). Lazy: until its chunk lands,
   openRecord() returns false and callers fall back to navigating, which is
   the documented contract — so nothing waits on it. */
const RecordDialogHost = lazy(() =>
  import("@/components/RecordDialogHost").then((m) => ({ default: m.RecordDialogHost })),
);
/* Opened from the bell only — its own chunk, off the first-load budget. */
const ReleaseNotesDialog = lazy(() =>
  import("@/components/ReleaseNotesDialog").then((m) => ({ default: m.ReleaseNotesDialog })),
);
const LivvyChat = lazy(() =>
  import("@/components/livvy/LivvyChat").then((m) => ({ default: m.LivvyChat })),
);
/* The ⌘K palette (flat-shell demo): its own chunk. */
const CommandPalette = lazy(() =>
  import("@/components/shell/CommandPalette").then((m) => ({ default: m.CommandPalette })),
);
import { BottomNav } from "@/components/BottomNav";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMarkOpenMenuChipSeen, type MenuChipSetting } from "@/lib/menuChipSeen";
import { apiRequest, fetchJson, queryClient } from "@/lib/queryClient";
import { ProjectAccessGate } from "@/components/ProjectAccessGate";
import { useCompanyEntitlements } from "@/lib/companyEntitlements";
import { userPreferencesQuery, setCachedUserPreferences } from "@/lib/userPreferences";
import { fetchContactPage } from "@/lib/contactList";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import { SyncStatusBanner } from "@/components/SyncStatusBanner";
import { TestProjectBanner } from "@/components/TestProjectBanner";
import { ServiceNotificationBanner } from "@/components/ServiceNotificationBanner";
import { openRecord } from "@/lib/openRecord";
import { useAuth } from "@/hooks/useAuth";
import { canAccessPlatformSettings } from "@/lib/platformAccess";
import { useAppBadge, UNREAD_COUNT_KEY } from "@/hooks/use-app-badge";
import type { ReleaseNote } from "@shared/schema";
import { useTranslation } from "@/hooks/useTranslation";
import { useNotificationText } from "@/hooks/useNotificationText";
import { menuVisibilityQuery } from "@/lib/shellQueries";
import { isApplePlatform, isFlatShell, menuShortcut, openCommand, readMenuHidden, rememberSpaceHue, applySpaceHue, watchHardwareKeyboard, TOGGLE_MENU_EVENT, writeMenuHidden } from "@/lib/shellFlat";
import { useProjectHue } from "@/lib/projectColour";
import { projectQuery } from "@/lib/projectsQuery";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PanelLeft, Pin } from "lucide-react";

interface AppShellProps {
  children: ReactNode;
}


let notificationTarget: Promise<typeof import("@shared/notificationTarget")> | undefined;
const loadNotificationTarget = () => (notificationTarget ??= import("@shared/notificationTarget"));

export function AppShell({ children }: AppShellProps) {
  const [location, setLocation] = useLocation();
  const { mode, projectId, projectName } = useUIContext();
  /* The flat shell (lib/shellFlat): one tinted ground under the menu and the
     header, the page raised on it. The tint follows the project. */
  const flat = useMemo(isFlatShell, []);
  // The project's colour (lib/projectColour): chosen, else from its logo.
  // Only on a project's own pages: the context keeps the last project after
  // the reader leaves it, so the Projects list and the settings areas wore that
  // project's colour instead of the app's own (Andrew, 2026-11-02).
  const onProjectPage = mode === "project" && /^\/projects\/[^/?#]+/.test(location);
  const { data: shellProject } = useQuery({ ...projectQuery(onProjectPage ? projectId : undefined) });
  const projectHue = useProjectHue(onProjectPage ? shellProject : null, onProjectPage ? projectId : null);
  /* Hide the menu (Arc's ⌘\\): the page takes the window; the menu peeks
     back out while the pointer rests on the window's left edge. */
  const [menuHidden, setMenuHidden] = useState(() => flat && readMenuHidden());
  const [menuPeek, setMenuPeek] = useState(false);
  /* The peek opens after a short dwell at the edge (brushing past does not
     open it) and closes a moment after the pointer leaves it (overshooting
     the panel does not snap it shut) — Notion's, Arc's and Linear's way. */
  const peekTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peekLater = useCallback((open: boolean, ms: number) => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setMenuPeek(open), ms);
  }, []);
  const peekStay = useCallback(() => {
    if (peekTimer.current) clearTimeout(peekTimer.current);
    peekTimer.current = null;
  }, []);
  useEffect(() => {
    if (!menuPeek) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuPeek(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuPeek]);
  const toggleMenu = useCallback(() => {
    setMenuPeek(false);
    setMenuHidden((h) => {
      writeMenuHidden(!h);
      return !h;
    });
  }, []);
  useEffect(() => {
    if (!flat) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        toggleMenu();
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(TOGGLE_MENU_EVENT, toggleMenu);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(TOGGLE_MENU_EVENT, toggleMenu);
    };
  }, [flat, toggleMenu]);
  /* The menu's button: ONE place, open or hidden — the window's top-left,
     centred on the breadcrumb row (Arc's place), its glyph in line with the
     menu rows' icons (the menu's 12px edge + 12px + 12px, less the button's
     own 11px around its glyph). Never in the page's column,
     where it pushed the breadcrumb off the content edge. */
  const menuToggle = () => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={toggleMenu}
          aria-label={menuHidden ? t('shellShowMenu', 'Show Menu') : t('shellHideMenu', 'Hide Menu')}
          aria-pressed={menuHidden}
          data-testid="button-toggle-menu"
          className="hit-target fixed z-notification hidden size-10 items-center justify-center rounded-full text-foreground/70 transition-colors duration-instant ease-standard hover:bg-foreground/[.06] hover:text-foreground sidenav:flex print:hidden"
          // ONE place, open or hidden: it never moves when pressed. With the menu hidden the frame's left edge comes to it (index.css --frame-gap).
          style={{ top: 'calc(var(--sa-top) + 8px)', left: 'calc(max(0.75rem, var(--sa-left)) + 14px)' }}
        >
          <PanelLeft className="size-[18px]" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {menuHidden ? t('shellShowMenu', 'Show Menu') : t('shellHideMenu', 'Hide Menu')}
        <kbd className="ml-2 font-sans opacity-70">{menuShortcut()}</kbd>
      </TooltipContent>
    </Tooltip>
  );
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("menu-hidden", flat && menuHidden);
    root.classList.toggle("menu-peek", flat && menuHidden && menuPeek);
  }, [flat, menuHidden, menuPeek]);
  // main.tsx starts it before the first paint; this covers any other entry (the harness). Once only.
  useEffect(() => watchHardwareKeyboard(), []);
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("shell-flat", flat);
    applySpaceHue(root, projectHue);
    if (flat && projectId && shellProject) rememberSpaceHue(projectId, projectHue);
  }, [flat, projectHue, projectId, shellProject]);
  // Prices in the project's own dollars: USD in Florida and Texas.
  const currency = useProjectCurrencySync(mode === "project" ? projectId : null);
  const timeZone = useProjectTimeZoneSync(mode === "project" ? projectId : null);
  const { intelligenceEnabled } = useIntelligence();
  const { isVisible } = useMenuVisibility();
  /* A menu item's New / Updated chip goes once the reader has opened it
     (lib/menuChipSeen). Same query key the sidebar reads: one fetch. */
  const { data: menuChipSettings } = useQuery<MenuChipSetting[]>(menuVisibilityQuery());
  useMarkOpenMenuChipSeen(location, menuChipSettings);
  const { theme } = useTheme();
  const { user } = useAuth();
  const t = useTranslation();
  const notificationText = useNotificationText(t);

  // Check if we're on the home selection page
  const isHomePage = location === '/home' || location === '/' || location === '/projects';

  // Contact search and notifications state
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  /* The routing table (shared/notificationTarget.ts) is needed only once the
     panel is open, so it loads then rather than in every first load; by the
     time a row can be tapped it has arrived, and a tap that beats it waits. */
  useEffect(() => {
    if (notificationsOpen) void loadNotificationTarget();
  }, [notificationsOpen]);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notificationsTab, setNotificationsTab] = useState<'unread' | 'all'>('unread');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mayAutoFocus = useAutoFocus();
  const [showReleaseNotes, setShowReleaseNotesState] = useState(false);
  const [releaseNotesArmed, setReleaseNotesArmed] = useState(false);
  const setShowReleaseNotes = (open: boolean) => {
    if (open) setReleaseNotesArmed(true);
    setShowReleaseNotesState(open);
  };
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Determine if dark mode is active
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Fetch latest release note
  const { data: latestRelease } = useQuery<ReleaseNote>({
    queryKey: ['/api/release-notes/latest'],
  });

  /* WHICH NOTIFICATIONS THE BELL SHOWS: every project's, wherever the reader
     is, each row labelled with its project (Andrew, 2026-10-02). It used to
     narrow to the open project, so a PropPay LX task was invisible from any
     other project — someone who signed in and landed elsewhere never saw it.
     A notification is for the PERSON; tapping one takes them into its
     project (the route sets the context) and to the right place in it.
     `openProjectId` is only for opening a contact in place when the row
     belongs to the project already on screen. */
  const openProjectId: string | undefined = mode === 'project' && !isHomePage && projectId ? projectId : undefined;
  const notificationsKey = ['/api/notifications', 'all'] as const;
  // Project names for the global bell's row labels — the picker's own query
  // (same key), so it is usually already in the cache.
  const isSuperAdminUser = user?.role === 'SUPER_ADMIN';
  const { data: bellProjects = [] } = useQuery<Array<{ id: string; projectName: string }>>({
    queryKey: ['/api/projects', isSuperAdminUser ? 'all' : 'user'],
    queryFn: ({ signal }) => fetchJson(isSuperAdminUser ? '/api/projects?all=true' : '/api/projects', { signal, background: true }),
    enabled: !!user,
  });
  const projectNameById = useMemo(() => new Map(bellProjects.map((p) => [p.id, p.projectName])), [bellProjects]);

  // Fetch contact notifications
  const { data: contactNotifications = [], refetch: refetchNotifications } = useQuery<any[]>({
    queryKey: notificationsKey,
    queryFn: ({ signal }) =>
      fetchJson<any[]>('/api/notifications', { signal, background: true }),
    enabled: !!user,
    // A minute's freshness: coming back to the app refreshes the bell, a
    // glance at another window does not (QA 2026-10-28: every focus asked).
    // Every read/clear invalidates this key, so it never waits on it.
    staleTime: 60_000,
    // 5-min poll + refetch on focus instead of tight 30s polling
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });


  // SaleFish Intelligence is a COMPANY upgrade since 2026-09-30: the reader's
  // company has it or does not, whichever project is open. It was a per-project
  // add-on read from /api/projects/:id/features. The server refuses the chat
  // to anyone whose company lacks it, so this only decides the launcher.
  const { data: entitlements } = useCompanyEntitlements(!!user && intelligenceEnabled);

  // Fetch user preferences for cross-device sync (lastSeenReleaseVersion)
  // Must be declared before getLastSeenVersion and hasUnreadRelease which reference it.
  const { data: userPrefs } = useQuery<Record<string, string>>({
    ...userPreferencesQuery,
    enabled: !!user,
  });

  const hasIntelligenceSubscription = entitlements?.salefishIntelligence === true;

  // Track if user has seen the latest version.
  // Server (via userPrefs) is the source of truth; localStorage is the fast local fallback.
  const getLastSeenVersion = () => {
    if (typeof window === 'undefined') return null;
    return userPrefs?.lastSeenReleaseVersion ?? localStorage.getItem('lastSeenReleaseVersion');
  };

  const markVersionAsSeen = (version: string) => {
    if (typeof window === 'undefined') return;
    // Update localStorage immediately for instant reactivity
    localStorage.setItem('lastSeenReleaseVersion', version);
    // Optimistic cache update — hasUnreadRelease recomputes without a network round-trip
    setCachedUserPreferences({ lastSeenReleaseVersion: version });
    // Persist to server (best-effort)
    apiRequest('PATCH', '/api/users/me/preferences', { lastSeenReleaseVersion: version }).catch(() => {});
  };

  // Recently viewed contacts tracking - scoped by user and project
  const MAX_RECENT_CONTACTS = 50; // Store up to 50 entries

  const getRecentlyViewedKey = () => {
    if (!user?.id || !projectId) return null;
    return `recentlyViewedContacts:${user.id}:${projectId}`;
  };

  const [recentlyViewedIds, setRecentlyViewedIds] = useState<{ contactId: string; timestamp: number }[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  /* Publishes --sidebar-space (index.css) while the shell is mounted: the
     docked sidebar's width plus its gap wherever `sidenav:` holds, 0 where the
     tab bar navigates (a phone, an upright iPad) and on every page
     without a shell (sign-in, public quote). Floating cards — nudges, toasts —
     centre on the content column with it rather than on the window, and <main>
     takes its margin from the same number. A layout effect, so the class is
     on <html> before the first paint and nothing shifts. */
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('has-sidebar');
    return () => root.classList.remove('has-sidebar');
  }, []);

  // Initialize recently viewed from localStorage and detect mobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Detect mobile
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load recently viewed when user/project changes
  useEffect(() => {
    const key = getRecentlyViewedKey();
    if (!key) {
      setRecentlyViewedIds([]);
      return;
    }
    
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        setRecentlyViewedIds(JSON.parse(stored));
      } else {
        setRecentlyViewedIds([]);
      }
    } catch (e) {
      console.error('Failed to load recently viewed contacts:', e);
      setRecentlyViewedIds([]);
    }
  }, [user?.id, projectId]);

  // Listen for contactViewed events from other components (Dashboard, Contacts page, etc.)
  useEffect(() => {
    const handleContactViewed = (event: CustomEvent<{ contactId: string }>) => {
      if (event.detail?.contactId) {
        addRecentlyViewed(event.detail.contactId);
      }
    };
    
    window.addEventListener('contactViewed', handleContactViewed as EventListener);
    return () => window.removeEventListener('contactViewed', handleContactViewed as EventListener);
  }, [user?.id, projectId, recentlyViewedIds]);

  const addRecentlyViewed = (contactId: string) => {
    if (typeof window === 'undefined') return;
    
    const key = getRecentlyViewedKey();
    if (!key) return;
    
    const now = Date.now();
    const newEntry = { contactId, timestamp: now };
    
    // Remove any existing entry for this contact and add new one at the start
    const updated = [newEntry, ...recentlyViewedIds.filter(r => r.contactId !== contactId)]
      .slice(0, MAX_RECENT_CONTACTS);
    
    setRecentlyViewedIds(updated);
    localStorage.setItem(key, JSON.stringify(updated));
  };

  const hasUnreadRelease = useMemo(() => {
    if (!latestRelease?.version) return false;
    const lastSeen = getLastSeenVersion();
    return lastSeen !== latestRelease.version;
    // userPrefs?.lastSeenReleaseVersion in deps so this recomputes when server data arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestRelease?.version, userPrefs?.lastSeenReleaseVersion]);

  // Check if there are unread contact notifications
  const unreadContactNotifications = useMemo(() => {
    return contactNotifications.filter((n: any) => !n.isRead);
  }, [contactNotifications]);
  
  // Filter notifications based on active tab
  const displayedNotifications = useMemo(() => {
    if (notificationsTab === 'unread') {
      return unreadContactNotifications;
    }
    return contactNotifications;
  }, [notificationsTab, unreadContactNotifications, contactNotifications]);

  // Combined unread indicator
  const hasUnreadNotifications = hasUnreadRelease || unreadContactNotifications.length > 0;
  const unreadTotal = unreadContactNotifications.length + (hasUnreadRelease ? 1 : 0);
  const phone = usePhone();

  /*
    Badge the installed app's Home Screen icon with the app-wide unread count.

    The release entry is added on top because the bell shows it as an unread
    item but it is not a `notifications` row — nothing writes a platform-wide
    notification (see the release-notes section of CLAUDE.md), so the server's
    count cannot include it. It is tracked per user as `lastSeenReleaseVersion`
    and resolved here, which is the only place that knows it.

    The consequence is a divergence worth knowing about: a push that arrives
    while the app is closed sets the badge from the server count alone, so an
    unread release is not counted until the app is next opened. Under-counting
    self-corrects on open; over-counting would leave a badge the user cannot
    clear by reading anything.
  */
  useAppBadge(hasUnreadRelease ? 1 : 0);

  /* Opening the latest update marks it READ — and it stays in the panel, read
     (no dot, regular weight), until the next version replaces it (Andrew,
     2026-09-27). It is not a `notifications` row, so "Clear all" and the row ×
     never remove it; the only thing that takes it out of the list is a newer
     release. Marked here as well as by the dialog so the bell, the badge and
     the Home Screen count all settle the moment it is opened. */
  const handleNotificationClick = () => {
    if (latestRelease?.version) {
      if (hasUnreadRelease) markVersionAsSeen(latestRelease.version);
      setSelectedVersion(latestRelease.version);
      setShowReleaseNotes(true);
      setNotificationsOpen(false);
    }
  };

  /* A NEW release opens its notes by itself, once — the bell's own path
     (handleNotificationClick), so it is marked seen exactly as a tap on the
     bell's entry marks it and the bell, the badge and the dialog agree.

     This is what the release push relies on: it carries no URL and lands on
     /home. The Dashboard's popup that was meant to do it read a
     `hasNewVersion` field /api/release-notes/latest has never sent, so it
     never opened. Waits for the server's preferences, which hold
     `lastSeenReleaseVersion`: deciding from this device's localStorage alone
     would reopen notes already read on another device. */
  const autoOpenedRelease = useRef<string | null>(null);
  useEffect(() => {
    const version = latestRelease?.version;
    if (!user || !version || !userPrefs || !hasUnreadRelease) return;
    if (autoOpenedRelease.current === version) return;
    autoOpenedRelease.current = version;
    handleNotificationClick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, latestRelease?.version, userPrefs, hasUnreadRelease]);

  // Mark release as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (version: string) => {
      if (!user?.id) throw new Error('User not found');
      return await apiRequest('POST', `/api/users/${user.id}/mark-release-seen`, { version });
    },
    onSuccess: () => {
      if (latestRelease?.version) {
        markVersionAsSeen(latestRelease.version);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/release-notes/latest'] });
    },
  });

  // Mark contact notification as read mutation
  const markContactNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return await apiRequest('PATCH', `/api/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });

  // Mark all notifications as read mutation (bulk operation)
  const markAllNotificationsMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('POST', '/api/notifications/mark-all-read', {});
    },
    /*
      The optimistic update below rewrites the panel's list. The Home Screen
      badge is its own query, so it has to come back from the server too.
    */
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    },
  });

  // Handle mark all as read with OPTIMISTIC updates for instant UI feedback
  const [clearingNotifications, setClearingNotifications] = useState(false);

  /** Removes one row — optimistic, then the server; the list and the badge
      both re-read on settle. */
  const handleClearNotification = async (id: string) => {
    queryClient.setQueryData(notificationsKey, (old: any[] | undefined) => old?.filter((n) => n.id !== id));
    try {
      await apiRequest('DELETE', `/api/notifications/${id}`);
    } catch (error) {
      console.error('Error clearing notification:', error);
    } finally {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    }
  };

  const handleClearAllNotifications = async () => {
    setClearingNotifications(true);
    queryClient.setQueryData(notificationsKey, () => []);
    try {
      await apiRequest('POST', '/api/notifications/clear', {});
    } catch (error) {
      console.error('Error clearing notifications:', error);
    } finally {
      setClearingNotifications(false);
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      // OPTIMISTIC UPDATE: Immediately update the cache to mark all notifications as read
      // This makes the UI respond instantly without waiting for the server
      queryClient.setQueryData(
        notificationsKey,
        (oldData: any[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((notification: any) => ({
            ...notification,
            isRead: true,
            readAt: new Date().toISOString()
          }));
        }
      );
      
      // Mark release notes as read if present (update localStorage immediately)
      if (latestRelease?.version && hasUnreadRelease) {
        markVersionAsSeen(latestRelease.version);
        // Also trigger the mutation in background
        markAsReadMutation.mutate(latestRelease.version);
      }
      
      // Fire the server mutation in the background (UI already updated)
      if (unreadContactNotifications.length > 0) {
        markAllNotificationsMutation.mutate();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // On error, refetch to get the correct state
      refetchNotifications();
    }
  };

  /* The sidebar is ALWAYS OPEN wherever `sidenav:` holds (768px up, except
     an upright touch screen below 1024px) and absent elsewhere, where the tab
     bar navigates — PropPay's split (Andrew, 2026-09-27; upright iPads moved
     to the tab bar 2026-10-31). There is no pinned/unpinned state, no drawer and no
     hamburger any more, so there is nothing to store or sync: the old
     `sidebarPinned` preference is simply no longer read. */
  const headerRef = useRef<HTMLElement>(null);

  // Publish the header's real height (content row + top safe-area inset) as
  // --app-header-height. Everything that has to clear the fixed header reads
  // this: .app-main's top padding and .sticky-below-header.
  //
  // useLayoutEffect + ResizeObserver rather than useEffect + resize: the
  // measurement lands before the browser paints (no visible jump from the
  // first-paint fallback in index.css), and a ResizeObserver also catches the
  // cases a resize event misses — rotating an iPhone, which changes the notch
  // inset without necessarily firing resize first, and the iOS status bar
  // growing during a call or screen recording.
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header || typeof window === 'undefined') return;

    const updateHeaderHeight = () => {
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${header.offsetHeight}px`
      );
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    // border-box: the window-controls band of an installed iPadOS app is this
    // header's PADDING (--sa-top), and a content-box observer never fired when
    // only the padding changed — window ↔ full screen left the variable stale.
    observer.observe(header, { box: 'border-box' });
    window.addEventListener('orientationchange', updateHeaderHeight);
    // …and the resize that switch arrives with, for engines that report the
    // padding change late or not at all to the observer.
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('orientationchange', updateHeaderHeight);
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  /* The header no longer hides on scroll (decision D7). A sticky solid bar is
     what makes a page feel anchored, and the transform was one more thing
     the scroll lock had to reason about. */

  // The menu's foot opens the account panel (SidebarFooter → openAccountPanel).
  // The command bar opens it on any of its tabs ("passkeys" lands on Security).
  const [accountTab, setAccountTab] = useState<AccountTab>("profile");
  useEffect(() => {
    const open = (e: Event) => {
      setNotificationsOpen(false);
      const asked = (e as CustomEvent).detail?.tab;
      setAccountTab(ACCOUNT_TABS.includes(asked) ? asked : "profile");
      setAccountMenuOpen(true);
    };
    window.addEventListener(OPEN_ACCOUNT_EVENT, open);
    return () => window.removeEventListener(OPEN_ACCOUNT_EVENT, open);
  }, []);

  // Debounce search for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 150);

  /*
    The search panel asks the server, and only while it is open.

    It used to read the project's whole contact list — fetched on EVERY screen
    of a project whether or not anyone opened search, 25 requests on a project
    of 2,500 contacts, and blind past that: a contact beyond the 2,500th could
    not be found from here at all. Now an open panel costs one request: the
    matches for what was typed (the same search as the Contacts page — every
    word, across name, emails, phones and company), the recently viewed by id,
    or the latest registrations.
  */
  const searchTerm = debouncedSearchQuery.trim();
  const recentIds = useMemo(() => recentlyViewedIds.slice(0, 10).map((r) => r.contactId), [recentlyViewedIds]);
  const searchPanelMode: 'results' | 'recently_viewed' | 'recent_registrations' | 'idle' =
    searchTerm ? 'results' : isMobile ? 'idle' : recentIds.length ? 'recently_viewed' : 'recent_registrations';
  const { data: searchPanelPage } = useQuery({
    queryKey: ['/api/contacts', projectId, 'search-panel', searchPanelMode, searchPanelMode === 'results' ? searchTerm : searchPanelMode === 'recently_viewed' ? recentIds.join(',') : ''],
    queryFn: ({ signal }) =>
      fetchContactPage(
        searchPanelMode === 'results'
          ? { projectId: projectId!, q: searchTerm, limit: 25 }
          : searchPanelMode === 'recently_viewed'
            ? { projectId: projectId!, ids: recentIds, limit: recentIds.length }
            : { projectId: projectId!, sort: 'createdAt', dir: 'desc', limit: 10 },
        signal,
      ),
    enabled: !!projectId && mode === 'project' && searchOpen && searchPanelMode !== 'idle',
    staleTime: 30_000,
    // Keep the last answer while typing, but never carry rows across projects or panel modes.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === projectId && prevQuery?.queryKey[3] === searchPanelMode ? prev : undefined,
  });

  const { displayContacts, displayMode } = useMemo(() => {
    const rows = searchPanelMode === 'idle' ? [] : (searchPanelPage?.contacts ?? []);
    if (searchPanelMode === 'recently_viewed') {
      // The server answers in its own order; the panel's is most recent first.
      const rank = new Map(recentIds.map((id, i) => [id, i]));
      const ordered = [...rows].sort((a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity));
      return { displayContacts: ordered, displayMode: 'recently_viewed' as const };
    }
    return {
      displayContacts: rows,
      displayMode: searchPanelMode === 'recent_registrations' ? ('recent_registrations' as const) : ('recently_viewed' as const),
    };
  }, [searchPanelMode, searchPanelPage, recentIds]);

  // Handle contact click - open detail modal and track as recently viewed
  const handleContactClick = (contact: any) => {
    // Track as recently viewed
    addRecentlyViewed(contact.id);
    
    // Ensure contact has the required fields for ContactDetailModal
    const enrichedContact = {
      ...contact,
      name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t('unnamedContact', 'Unnamed Contact'),
      email: contact.emailAddresses?.[0]?.email || '',
      phone: contact.phoneNumbers?.[0]?.number || ''
    };
    setSelectedContact(enrichedContact);
    setIsContactModalOpen(true);
    setSearchOpen(false);
    setSearchQuery("");
  };


  // Which MODE the shell is in is navigation state, not data. It was two ocean
  // hues, which made a mode chip look like a buyer-journey stage or a unit
  // status. The brand accent marks the non-default mode; the default is quiet.

  /* The search panel — the notification panel's shell (header, list,
     full-bleed rows) with the field in the header. Empty, it shows the
     recently viewed contacts (or the latest registrations). */
  const searchHeading = debouncedSearchQuery.trim()
    ? t('results', 'Results')
    : displayMode === 'recently_viewed'
      ? t('shellAppSearchRecentlyViewed', 'Recently Viewed')
      : t('shellAppSearchRecentRegistrations', 'Recent Registrations');
  const searchPanel = (
    <>
      <header className="bar-edge relative z-raised flex shrink-0 items-center gap-2 py-2 pl-3 pr-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            ref={searchInputRef}
            type="search"
            enterKeyHint="search"
            placeholder={t('shellAppSearchPlaceholder', 'Search contacts…')}
            aria-label={t('shellAppSearchContacts', 'Search Contacts')}
            className="pl-9"
            data-testid="input-search-contacts"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <CloseButton label={t('shellAppSearchClose', 'Close Search')} onClick={() => { setSearchOpen(false); setSearchQuery(""); }} />
      </header>
      <div className="flex shrink-0 items-baseline justify-between px-4 pb-1 pt-3">
        <h2 className="type-eyebrow text-muted-foreground">{searchHeading}</h2>
        <span className="text-caption tabular-nums text-muted-foreground">{displayContacts.length}</span>
      </div>
      <div className="momentum min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {displayContacts.length > 0 ? (
          <ul className="divide-y divide-border/70">
            {displayContacts.map((contact: any) => {
              const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || t('unnamedContact', 'Unnamed Contact');
              const email = contact.emailAddresses?.[0]?.email || '';
              const phoneNumber = contact.phoneNumbers?.[0]?.number || '';
              return (
                <li key={contact.id}>
                  <button
                    type="button"
                    onClick={() => handleContactClick(contact)}
                    data-testid={`search-contact-${contact.id}`}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-instant ease-standard hover:bg-foreground/[.04] focus-visible:bg-foreground/[.04] focus-visible:outline-hidden"
                  >
                    <LetterAvatar name={fullName} size={32} flat />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-label font-semibold text-foreground">{fullName}</span>
                      <span className="block truncate text-caption text-muted-foreground">{email || phoneNumber || t('shellAppSearchNoContactInfo', 'No contact info')}</span>
                    </span>
                    <EngagementBadge score={contact.engagementScore} />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <UserSearch className="size-7 text-muted-foreground" aria-hidden="true" />
            <p className="text-label font-semibold">{debouncedSearchQuery.trim() ? t('noContactsFound', 'No Contacts Found') : t('noContactsYet', 'No Contacts Yet')}</p>
            {debouncedSearchQuery.trim() && <p className="text-caption text-muted-foreground">{t('shellAppSearchNoResultsHint', 'Try a name, an email or a phone number.')}</p>}
          </div>
        )}
      </div>
    </>
  );

  /*
    THE NOTIFICATION PANEL — PropPay's, part for part (Andrew, 2026-09-27).

    One panel in two shells: a POPOVER hanging from the bell on a tablet or a
    desk (384px, capped at 560 tall), a bottom SHEET on a phone, where a
    popover pinned under the header filled the width anyway and read as a
    sheet that had failed to reach the bottom. Header: the title, the gear
    (notification settings) and a close. One list, full-bleed rows divided by
    hairlines, the unread ones tinted with a dot and a clear × on each. Footer:
    "Mark all read" and "Clear all" together — the two actions that act on
    everything in the list.
  */
  const notificationTitle = t('notifications', 'Notifications');
  const notificationPanel = (
    <>
      <header className="bar-edge relative z-raised flex shrink-0 items-center gap-2 px-4 py-3">
        <h2 className="flex-1 text-label font-bold">{notificationTitle}</h2>
        <button
          type="button"
          onClick={() => {
            setNotificationsOpen(false);
            window.dispatchEvent(new CustomEvent(OPEN_ACCOUNT_EVENT, { detail: { tab: "notifications" } }));
          }}
          aria-label={t('shellAppNotificationSettings', 'Notification Settings')}
          className="hit-target flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors duration-instant ease-standard hover:bg-foreground/[.06]"
          data-testid="button-notification-settings"
        >
          <Settings2 className="size-4" aria-hidden="true" />
        </button>
        <CloseButton label={t('shellAppNotificationsClose', 'Close Notifications')} onClick={() => setNotificationsOpen(false)} />
      </header>
      <div className="momentum min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="notification-panel">
            <ul className="divide-y divide-border/70">
              {contactNotifications.map((notification: any) => {
                const isTaskNotification = notification.type === 'task_due' || notification.type === 'task_overdue';

                // Pick icon based on notification type
                const Icon = (() => {
                  switch (notification.type) {
                    case 'task_due':
                    case 'task_overdue':
                      return CheckSquare;
                    case 'new_contact_registration':
                      return UserPlus;
                    case 'lead_assigned':
                      return UserSearch;
                    case 'new_offer':
                    case 'worksheet_approved':
                    case 'worksheet_archived':
                    case 'worksheet_waiting':
                      // A worksheet is drawn as one everywhere (lib/icons; Andrew, 2026-10-27).
                      return WorksheetIcon;
                    case 'contact_updated':
                    case 'summary_daily':
                    case 'summary_weekly':
                    case 'sell_through_milestone':
                    case 'units_released':
                      return TrendingUp;
                    case 'price_change_proposed':
                      return CircleDollarSign;
                    case 'sale_made':
                    case 'sale_new':
                    case 'sale_firm':
                    case 'sale_closed':
                      return Trophy;
                    case 'appointment_reminder':
                    case 'deposit_due':
                    case 'conditions_expiring':
                    case 'irrevocable_expiring':
                    case 'closing_upcoming':
                      return Calendar;
                    case 'contact_inactivity':
                      return Clock;
                    case 'billing_alert':
                      return CreditCard;
                    // The Email Marketing menu item's own icon.
                    case 'email_campaign_sent':
                    case 'email_campaign_results':
                    case 'email_lead_replied':
                      return Mails;
                    // Livvy's own icon, as everywhere she appears.
                    case 'livvy_draft':
                    case 'livvy_handoff':
                      return Mic;
                    case 'sale_rescinded':
                    case 'deposit_overdue':
                    case 'deposit_problem':
                    case 'mortgage_missing':
                    case 'worksheet_send_failed':
                    case 'sale_missing_from_salefish':
                    case 'fintrac_id_missing':
                    case 'salefish_sync_failing':
                    case 'pii_key_missing':
                    case 'email_list_spike':
                    case 'email_connection_failing':
                      return AlertTriangle;
                    default:
                      return Bell;
                  }
                })();

                // Determine if task is overdue or due today
                let taskStatus: 'overdue' | 'due-today' | null = null;
                if (notification.type === 'task_overdue') {
                  taskStatus = 'overdue';
                } else if (isTaskNotification && notification.taskDueDate) {
                  const now = new Date();
                  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  const taskDueDate = new Date(notification.taskDueDate);
                  taskStatus = taskDueDate < startOfToday ? 'overdue' : 'due-today';
                }
                const when = relativeTime(notification.createdAt, new Date(), t);
                const tone = taskStatus === 'overdue' ? 'bg-destructive/10 text-destructive' : taskStatus === 'due-today' ? 'bg-tint-warning text-on-tint-warning' : 'bg-primary/10 text-primary';

                return (
                  <li key={notification.id} className="group relative">
                    <button
                      type="button"
                      onClick={() => {
                        /* Mark read OPTIMISTICALLY and navigate at once. This
                           used to await the PATCH (and, for a task, a second
                           one) before moving, so on a phone a tapped row sat
                           there for a full round trip — a cold serverless
                           start included — looking like the tap had missed.
                           The list and the badge re-read when the write
                           settles, which also corrects the row if it failed. */
                        queryClient.setQueryData(notificationsKey, (old: any[] | undefined) =>
                          old?.map((n) => (n.id === notification.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)),
                        );
                        const writes: Promise<unknown>[] = [
                          apiRequest('PATCH', `/api/notifications/${notification.id}/read`).catch((error) => {
                            console.error('Error marking notification as read:', error);
                          }),
                        ];

                        /* In-app routing, not `window.location.href` (A31): a
                           full page load threw away the whole app — every
                           cached query, the open project, the scroll position —
                           to show a contact the router can open in place. The
                           panel closes first so its focus return does not land
                           on a bell that is about to be re-rendered. */
                        /* Where it opens is decided in ONE place for the bell
                           and the push alike (shared/notificationTarget.ts),
                           and it always answers: a row that matched no branch
                           here used to do nothing but close the panel — a
                           task with no contact, a quote (2026-10-02). */
                        if (isTaskNotification && notification.taskId && notification.contactId) {
                          writes.push(
                            apiRequest('PATCH', `/api/contacts/${notification.contactId}/tasks/${notification.taskId}/mark-notification-read`).catch((error) => {
                              console.error('Error marking task notification as read:', error);
                            }),
                          );
                        }
                        void Promise.all(writes).finally(() => {
                          queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
                          queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
                        });
                        setNotificationsOpen(false);
                        // A contact opens IN PLACE over whatever the reader was
                        // doing (G12) — the page underneath stays put. The route
                        // is the fallback when no record host is mounted.
                        // Only within the project already open: another
                        // project's row goes to that project, which sets its
                        // context, and opens the contact there.
                        void loadNotificationTarget().then(({ notificationPath }) => {
                          const to = notificationPath(notification, { platformAdmin: canAccessPlatformSettings(user) });
                          const opensContact = `/projects/${notification.projectId}/contacts?openContact=`;
                          if (
                            to.startsWith(opensContact) &&
                            openProjectId && notification.projectId === openProjectId &&
                            openRecord({
                              type: 'contact',
                              id: notification.contactId,
                              projectId: notification.projectId,
                              tab: new URLSearchParams(to.slice(to.indexOf('?'))).get('tab') ?? undefined,
                            })
                          ) return;
                          setLocation(to);
                        });
                      }}
                      className={cn(
                        "flex w-full items-start gap-3 py-3 pl-4 pr-11 text-left transition-colors duration-instant ease-standard focus-visible:outline-hidden focus-visible:bg-foreground/[.04]",
                        /* Unread keeps its tint on hover instead of being
                           replaced by the grey one. `hover:bg-muted/50` on a
                           tinted row would take the tint AWAY on the way to
                           clicking it, so the one row you are reaching for is
                           the one that stops looking unread. */
                        notification.isRead
                          ? "hover:bg-foreground/[.04]"
                          : "bg-primary/[.05] hover:bg-primary/[.08]"
                      )}
                      data-testid={`notification-${isTaskNotification ? 'task' : 'contact'}-${notification.id}`}
                    >
                      <span className={cn("mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full", tone)} aria-hidden="true">
                        <Icon className="size-[15px]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        {/* Nothing here is cut off. The title used to `truncate`
                            to one line and the message to `line-clamp-2`, which
                            on a 390px panel meant a row read "A conditional offer
                            on suite…" — the notification withheld the very thing
                            it existed to say, and there was nowhere to go to read
                            the rest, because tapping navigates to the contact
                            rather than expanding the row.

                            `break-words` because a title is user-adjacent copy: a
                            long unbroken token (a URL, a 40-character suite code)
                            would otherwise push the row wider than the panel and
                            reintroduce the clipping by another route. */}
                        {/* Every row says whose it is — the eyebrow PropPay's
                            bell carries — inside a project too, since the
                            list is every project's. */}
                        {projectNameById.get(notification.projectId) && (
                          <span className="type-eyebrow mb-0.5 block truncate text-muted-foreground">
                            {projectNameById.get(notification.projectId)}
                          </span>
                        )}
                        <span className="flex items-baseline gap-2">
                          <span className={cn("min-w-0 flex-1 break-words text-label", notification.isRead ? "font-medium" : "font-bold")}>
                            {notificationText(notification.title)}
                          </span>
                          <time dateTime={notification.createdAt} title={when.full} className="shrink-0 text-micro tabular-nums text-muted-foreground">
                            {when.short}
                          </time>
                        </span>
                        {notification.message && (
                          /* The TITLE is never cut — it carries the subject, and
                             a row whose subject is elided is a row you cannot
                             triage. The message is supporting detail, so it
                             clamps at three lines: enough to get the gist,
                             bounded so one long note cannot push six other
                             notifications off the panel. Tapping the row opens
                             the record where the full text lives.

                             NO `block` here. `line-clamp-*` works by setting
                             `display: -webkit-box`, and a `block` beside it wins
                             on source order and silently disables the clamp —
                             which is what `line-clamp-2 block` had been doing
                             here all along. It read as working only because the
                             harness fixture supplied `body` where this reads
                             `message`, so no message ever rendered to overflow. */
                          <span className="mt-0.5 line-clamp-3 break-words text-caption leading-snug text-muted-foreground">
                            {notificationText(notification.message)}
                          </span>
                        )}
                        {taskStatus && (
                          <span className={cn("mt-1 inline-block text-micro font-semibold", taskStatus === 'overdue' ? "text-destructive" : "text-on-tint-warning")}>
                            {taskStatus === 'overdue' ? t('overdue', 'Overdue') : t('shellAppNotificationDueToday', 'Due Today')}
                          </span>
                        )}
                      </span>
                      {!notification.isRead && (
                        <span aria-hidden="true" className="mt-2 size-2 shrink-0 rounded-full bg-primary" />
                      )}
                    </button>
                    {/* Clear sits over the row, not inside it — a button in a
                        button is invalid and un-clickable in Safari. Always
                        visible on touch (a phone never hovers), revealed on
                        hover where there is one; the row reserves the space
                        (pr-11) either way so nothing reflows. */}
                    <button
                      type="button"
                      onClick={() => handleClearNotification(notification.id)}
                      disabled={clearingNotifications}
                      aria-label={t('shellAppNotificationClear', 'Clear Notification: {title}', { title: notificationText(notification.title) })}
                      className="hit-target absolute right-2 top-2.5 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-opacity duration-instant ease-standard hover:bg-foreground/[.08] focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:hover)]:opacity-0"
                      data-testid={`button-clear-notification-${notification.id}`}
                    >
                      <X className="size-3.5" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}

              {/* System Upgrade notification */}
              {latestRelease && (
                <li>
                  <button
                    type="button"
                    onClick={handleNotificationClick}
                    className={cn(
                      "flex w-full items-start gap-3 py-3 pl-4 pr-4 text-left transition-colors duration-instant ease-standard focus-visible:outline-hidden focus-visible:bg-foreground/[.04]",
                      hasUnreadRelease
                        ? "bg-primary/[.05] hover:bg-primary/[.08]"
                        : "hover:bg-foreground/[.04]"
                    )}
                    data-testid="notification-release"
                  >
                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
                      <SystemUpgradeIcon className="size-[15px]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className={cn("min-w-0 flex-1 break-words text-label", hasUnreadRelease ? "font-bold" : "font-medium")}>
                          {t('shellAppNotificationSystemUpgrade', 'System Upgrade')}
                        </span>
                        <time dateTime={String(latestRelease.releaseDate)} className="shrink-0 text-micro tabular-nums text-muted-foreground">
                          {relativeTime(latestRelease.releaseDate, new Date(), t).short}
                        </time>
                      </span>
                      {/* The version sits on the second line, not beside the
                          title. The title row already spends its width on the
                          timestamp, so "System upgrade · v0.3.4" truncated —
                          and the version is the half that got cut, which is the
                          one piece of it a reader is actually looking for. */}
                      <span className="mt-0.5 block text-caption leading-snug text-muted-foreground">
                        <span className="font-medium tabular-nums text-foreground/80">v{latestRelease.version}</span>
                        {" · "}
                        {t('shellAppNotificationSeeWhatsNew', "See What's New in This Upgrade")}
                      </span>
                    </span>
                    {hasUnreadRelease && <span aria-hidden="true" className="mt-2 size-2 shrink-0 rounded-full bg-primary" />}
                  </button>
                </li>
              )}
            </ul>

            {/* Empty State */}
            {!latestRelease && contactNotifications.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
                <Inbox className="size-7 text-muted-foreground" aria-hidden="true" />
                <p className="text-label font-semibold">{t('shellAppNotificationsEmptyTitle', "You're all caught up")}</p>
                <p className="text-caption text-muted-foreground">
                  {t('shellAppNotificationsEmptyBody', 'New registrations, tasks coming due and anything else needing attention, across all your projects, will appear here.')}
                </p>
              </div>
            )}
      </div>
      {(contactNotifications.length > 0 || hasUnreadRelease) && (
        <footer className="shrink-0 border-t border-border/70 px-2 py-2">

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={unreadContactNotifications.length === 0 && !hasUnreadRelease}
                className="flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded px-3 py-2 text-caption font-semibold text-primary-ink transition-colors duration-instant ease-standard hover:bg-primary/[.08] disabled:opacity-40 disabled:hover:bg-transparent"
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" /> {t('markAllRead', 'Mark All Read')}
              </button>
              <span aria-hidden="true" className="h-4 w-px shrink-0 bg-border" />
              <button
                type="button"
                onClick={handleClearAllNotifications}
                disabled={clearingNotifications}
                className="flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded px-3 py-2 text-caption font-semibold text-muted-foreground transition-colors duration-instant ease-standard hover:bg-foreground/[.05] hover:text-foreground disabled:opacity-50"
                data-testid="button-clear-all-notifications"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> {t('shellAppNotificationsClearAll', 'Clear All')}
              </button>
            </div>
        </footer>
      )}
    </>
  );

  const modeColor = mode === 'global'
    ? 'bg-gradient-to-b from-primary/15 to-primary/5 text-primary'
    : 'bg-muted/40 text-muted-foreground';

  // Mock topBarHeight for demonstration purposes, in a real app this would be dynamic
  const topBarHeight = '64px'; // Example height, adjust as needed

  return (
    <>
      <SkipNavigation />

      {/* Main content - responsive width based on sidebar state.
          100dvh (not 100vh) so the mobile browser's collapsing toolbar doesn't
          make every page scrollable by the height of that toolbar. */}
      {/* data-shell-page: what a settings layer (SettingsLayerModal) makes
          inert while it is up — the page column and the docked sidebar. */}
      <div data-shell-page="" className="shell-column min-h-[100dvh] flex flex-col sidenav:ml-[var(--sidebar-space)] print:ml-0">
        {/* Header: STICKY, SOLID, IN FLOW.

            It used to be `fixed`, translucent with a backdrop-filter, rounded
            at the bottom, and it slid out of view on scroll. Each of those was
            a tell. The blur washed scrolling content out as a milky haze and
            WebKit renders it far heavier than Chromium; in an installed PWA it
            sat directly under a sharp system clock and read as the top of the
            app being out of focus. `fixed` meant every page had to reserve the
            header's height in its own padding; sticky takes the space it
            occupies. And a bar that hides is a page that feels unanchored
            (decision D7). It still publishes its measured height as
            --app-header-height for anything that stacks under it. */}
        <header
          ref={headerRef}
          className="app-header bar-edge sticky top-0 z-chrome shrink-0 flex-col bg-background print:hidden"
          style={{ paddingTop: 'var(--sa-top)' }}
          onClick={(e) => {
            // Tap anywhere on the header background to scroll to top (iOS-style)
            const target = e.target as HTMLElement;
            if (!target.closest('button, a, input, select, [role="button"], [role="tab"], [tabindex]')) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
        >
          {/* No top-edge strip of its own: the ONE root strip (App.tsx) is the
              sample, in this bar's colour — see ui/top-edge-sample. A copy here
              painted a 12% wash over the top 12px of the bar. */}
          {/* 57px content row — sits below notch/Dynamic Island.
              Uses the same --gutter as <main> so the menu button lines up
              with the page content underneath at every breakpoint. */}
          <div className="flex items-center justify-between gap-2 sm:gap-3 lg:gap-4 h-14 safe-px">
          <div className="flex items-baseline gap-2 sm:gap-3 lg:gap-4 min-w-0 flex-1">
            {/* Every width, as PropPay's: on a phone it is the grid and the
                project (the way back to the picker and to the dashboard). */}
            <div className="min-w-0 flex-1 overflow-hidden">
              <Breadcrumbs />
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Search — an icon, not a field (Andrew, 2026-09-27). It opens in
                the notification panel's shell: a popover from the icon on a
                tablet or a desk, a bottom sheet on a phone. */}
            {mode === 'project' && !isHomePage && (() => {
              const trigger = (onClick?: () => void) => (
                <button
                  type="button"
                  onClick={onClick}
                  aria-label={flat ? t('shellCmdTitle', 'Go Anywhere') : t('shellAppSearchContacts', 'Search Contacts')}
                  aria-haspopup="dialog"
                  aria-expanded={searchOpen}
                  data-testid="button-search-contacts"
                  className={`hit-target flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full text-foreground/70 transition-colors duration-instant ease-standard hover:bg-foreground/[.06] hover:text-foreground ${flat ? 'min-w-10 px-2.5' : 'w-10'}`}
                >
                  <Search className="size-[18px]" aria-hidden="true" />
                  {/* The shortcut, where there is a keyboard to press it: a desk. */}
                  {flat && <kbd className="kbd-hint rounded-sm border border-foreground/15 px-1.5 py-px font-sans text-micro tabular-nums text-muted-foreground">{isApplePlatform() ? '⌘K' : 'Ctrl K'}</kbd>}
                </button>
              );
              if (flat) return trigger(() => { setNotificationsOpen(false); openCommand(); });
              if (phone) return trigger(() => { setSearchOpen(true); setNotificationsOpen(false); });
              return (
                <PopoverPrimitive.Root
                  open={searchOpen}
                  onOpenChange={(o) => { setSearchOpen(o); if (o) setNotificationsOpen(false); else setSearchQuery(""); }}
                >
                  <PopoverPrimitive.Trigger asChild>{trigger()}</PopoverPrimitive.Trigger>
                  <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Content
                      align="end"
                      sideOffset={10}
                      collisionPadding={12}
                      aria-label={t('shellAppSearchContacts', 'Search Contacts')}
                      onOpenAutoFocus={(e) => {
                        // Straight into the field where a keyboard is already there.
                        e.preventDefault();
                        if (mayAutoFocus) searchInputRef.current?.focus();
                      }}
                      className="surface-elevated z-popover flex max-h-[min(560px,calc(100dvh-var(--app-header-height)-24px))] w-[min(384px,calc(100vw-24px))] flex-col overflow-clip rounded-lg text-card-foreground outline-hidden data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95"
                    >
                      {searchPanel}
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Portal>
                </PopoverPrimitive.Root>
              );
            })()}

            {/* Ideas — PropPay's lightbulb: feedback in two taps, from wherever
                the reader noticed the thing (sent to hello@salefish.app). */}
            <FeedbackButton projectId={mode === 'project' ? projectId : null} />

            {/* The bell — PropPay's: a 40px circle around an 18px glyph, the
                count hanging off the BELL's shoulder rather than the button's
                corner. On a phone it opens the sheet below; from 640px up the
                panel is a popover anchored here. */}
            {isVisible('notifications') && (() => {
              const bell = (onClick?: () => void) => (
                <button
                  type="button"
                  onClick={onClick}
                  aria-label={unreadTotal > 0 ? t('shellAppNotificationsUnreadLabel', 'Notifications — {count} Unread', { count: unreadTotal }) : t('notifications', 'Notifications')}
                  aria-haspopup="dialog"
                  aria-expanded={notificationsOpen}
                  data-testid="button-notifications"
                  className="hit-target relative flex size-10 shrink-0 items-center justify-center rounded-full text-foreground/70 transition-colors duration-instant ease-standard hover:bg-foreground/[.06] hover:text-foreground"
                >
                  {/* min-w-0: the badge hangs off the bell's shoulder on purpose. */}
                  <span className="relative min-w-0">
                    <Bell className="size-[18px]" aria-hidden="true" />
                    <NotificationBadge count={unreadTotal} noun="unread notification" decorative className="-ml-2.5 -top-2" />
                  </span>
                </button>
              );
              if (phone) return bell(() => { setNotificationsOpen(true); setAccountMenuOpen(false); });
              return (
                <PopoverPrimitive.Root
                  open={notificationsOpen}
                  onOpenChange={(o) => { setNotificationsOpen(o); if (o) setAccountMenuOpen(false); }}
                >
                  <PopoverPrimitive.Trigger asChild>{bell()}</PopoverPrimitive.Trigger>
                  <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Content
                      align="end"
                      sideOffset={10}
                      collisionPadding={12}
                      aria-label={notificationTitle}
                      className="surface-elevated z-popover flex max-h-[min(560px,calc(100dvh-var(--app-header-height)-24px))] w-[min(384px,calc(100vw-24px))] flex-col overflow-clip rounded-lg text-card-foreground outline-hidden data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95"
                    >
                      {notificationPanel}
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Portal>
                </PopoverPrimitive.Root>
              );
            })()}

            {/* The account panel. Its trigger is the menu's foot (SidebarFooter)
                — PropPay keeps the person at the bottom-left of the menu, on a
                desk and in the phone's sheet — so the header carries no avatar. */}
            <UserAccountMenu 
              hideTrigger
              initialTab={accountTab}
              isOpen={accountMenuOpen}
              onOpenChange={(isOpen) => {
                setAccountMenuOpen(isOpen);
                if (isOpen) {
                  setNotificationsOpen(false); // Close notifications when opening account menu
                }
              }}
            />
          </div>
          </div>{/* end 57px content row */}
          {/* The service banner, UNDER the content row: the top of the screen
              is then always this bar's own ground — the colour the root
              top-edge strip is drawn in — and the window-controls band of an
              iPadOS window is padded once, by this header, not again by the
              banner. Pinned with the header; its height lands in
              --app-header-height through the observer above. */}
          <ServiceNotificationBanner />
          {/* The page's two top corners, under the header (see page-frame-bottom). */}
          <span aria-hidden="true" className="page-frame-top" />
        </header>

        {/* Main Content Area */}
        {/* .app-main owns ALL of the page's outer spacing — header offset, side
            gutters (widened for a landscape notch) and the bottom home-indicator
            inset. Page components render their content flush and must not add
            their own outer padding, or the two stack up. */}
        {/* tabIndex -1: the Router moves focus here on a path change (G3), so
            a screen reader lands on the new page rather than the old link. */}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 app-main outline-hidden"
          style={{ containerType: 'inline-size' }}
          role="main"
          aria-label={t('shellAppMainContent', 'Main Content')}
        >
          {/* A SaleFish-synced project whose last sync failed or is old says so
              above every page (E7): "Showing data as of 2:15 p.m." */}
          {/* A project this reader is not on: one clean refusal instead of the
              page and its live buttons (components/ProjectAccessGate). */}
          <ProjectAccessGate projectId={mode === 'project' ? projectId : null}>
            {/* A project marked Test says so above every page, so sample data is never read as a builder's. */}
            {mode === 'project' && projectId && <TestProjectBanner projectId={projectId} className="mb-stack" />}
            {mode === 'project' && projectId && <SyncStatusBanner projectId={projectId} className="mb-stack" />}
            {/* Keyed on the currency and zone: a project whose dollars or clock arrive after a first paint redraws every figure and time in them. */}
            <Fragment key={`${currency}|${timeZone}`}>{children}</Fragment>
          </ProjectAccessGate>
        </main>
        {/* The page's frame at the window's foot (index.css, "the page is a
            frame"): a band of the ground pinned to the bottom with the page's
            two bottom corners, so the page always ends rounded and scrolls
            between the header and this. */}
        <div aria-hidden="true" className="page-frame-bottom" />
        {/* The light theme's drop shadow, on the FRAME rather than the
            scrolling page, so it falls the same at every scroll position. */}
        <div aria-hidden="true" className="page-frame-shadow" />

      </div>

      {/* The tab bar — wherever `tabnav:` holds (a phone, an upright iPad
          below 1024px), on a project's own pages;
          off a project it is one Menu tab opening the same sheet. It is the
          navigation there; the sidebar is not rendered. */}
      <BottomNav />

      {/* The sidebar: always open wherever `sidenav:` holds (768px up, but
          not an upright touch screen below 1024px — that has the tab bar), a floating panel in the chrome layer — PropPay's widths, 210px
          then 244px from lg. Not rendered below it at all, so none of its
          links sit in the tab order behind the tab bar. Part of the layout,
          not something covering the page: no scrim, no scroll lock.
          236px, not PropPay's 210, at the iPad width: SaleFish's longest rows
          ("Company settings" with its external-link glyph) do not fit 210 —
          tried again 2026-10-28 and they wrapped to two lines. A row too long
          for its width wraps rather than truncating (MenuRowLabel).
          --sidebar-space in index.css follows it. */}
      <div
        className="menu-panel hidden sidenav:flex fixed w-[236px] lg:w-[244px] rounded-panel z-chrome flex-col overflow-clip print:hidden"
        style={{
          top: 'calc(0.75rem + var(--sa-top))',
          bottom: 'calc(0.75rem + var(--sa-bottom))',
          left: 'max(0.75rem, var(--sa-left))',
        }}
        data-testid="sidebar-docked"
        data-shell-page=""
        onMouseEnter={() => { if (menuHidden) peekStay(); }}
        onMouseLeave={() => { if (menuHidden) peekLater(false, 300); }}
        inert={flat && menuHidden && !menuPeek}
      >
        {/* Peeking: a pin in the panel's own top strip keeps the menu open (Notion's). */}
        {flat && menuHidden && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleMenu}
                aria-label={t('shellKeepMenuOpen', 'Keep the Menu Open')}
                data-testid="button-pin-menu"
                className="hit-target absolute right-2.5 top-1.5 z-raised flex size-8 items-center justify-center rounded-full text-sidebar-muted outline-hidden transition-colors duration-instant ease-standard hover:bg-[hsl(var(--menu-wash)/0.08)] hover:text-sidebar-strong focus-visible:ring-2 focus-visible:ring-sidebar-on-active"
              >
                <Pin className="size-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {t('shellKeepMenuOpen', 'Keep the Menu Open')}
              <kbd className="ml-2 font-sans opacity-70">{menuShortcut()}</kbd>
            </TooltipContent>
          </Tooltip>
        )}
        <ModeAwareSidebar />
      </div>

      {/* With the menu hidden: rest the pointer on the window's left edge to
          bring it back over the page, as in Arc. */}
      {flat && menuHidden && (
        <div
          aria-hidden="true"
          className="fixed bottom-0 left-0 z-chrome hidden w-3 sidenav:block"
          style={{ top: 'calc(var(--sa-top) + 3.5rem)' }}
          onMouseEnter={() => peekLater(true, 120)}
          onMouseLeave={() => peekLater(false, 300)}
        />
      )}
      {flat && menuToggle()}

      {/* Livvy, the assistant (was SaleFish Intelligence) - only shows when:
          1. Livvy is enabled in Platform Settings
          2. A project is selected
          3. The reader's company has Livvy AI Assistant on its plan */}
      {intelligenceEnabled && projectId && projectName && mode === 'project' && hasIntelligenceSubscription && (
        /* Outside <main>, so the Router's own ChunkErrorBoundary does not reach
           here: without one, a chunk that fails to load after a deploy takes
           the whole app down rather than this one control. */
        <ChunkErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <LivvyChat
              projectId={projectId}
              projectName={projectName}
              inPalette={flat}
            />
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {flat && (
        <ChunkErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <CommandPalette
              projectId={mode === 'project' ? projectId ?? null : null}
              livvyAvailable={!!(intelligenceEnabled && projectId && mode === 'project' && hasIntelligenceSubscription)}
              onOpenContact={handleContactClick}
            />
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {/* Search on a phone: a bottom sheet, like the notifications. From 640px
          up it is a popover hanging from the search icon (header, above). */}
      {mode === 'project' && !isHomePage && phone && (
        <Dialog open={searchOpen} onOpenChange={(o) => { setSearchOpen(o); if (!o) setSearchQuery(""); }}>
          <DialogContent size="md" className="max-h-[80dvh]" hideClose>
            <DialogHeader className="sr-only">
              <DialogTitle>{t('shellAppSearchContacts', 'Search Contacts')}</DialogTitle>
              <DialogDescription>{t('shellAppSearchDescription', 'Find a contact in this project')}</DialogDescription>
            </DialogHeader>
            {searchPanel}
          </DialogContent>
        </Dialog>
      )}

      {/* The notification panel on a phone: a bottom sheet. From 640px up it
          is a popover hanging from the bell (in the header, above). */}
      {isVisible('notifications') && phone && (
        <Dialog open={notificationsOpen} onOpenChange={setNotificationsOpen}>
          {/* hideClose — the panel's own header carries the close. */}
          <DialogContent size="md" className="max-h-[80dvh]" hideClose>
            <DialogHeader className="sr-only">
              <DialogTitle>{notificationTitle}</DialogTitle>
              <DialogDescription>{t('shellAppNotificationsDescription', 'Your notifications')}</DialogDescription>
            </DialogHeader>
            {notificationPanel}
          </DialogContent>
        </Dialog>
      )}

      {/* System Upgrades dialog — mounted from its first open and kept, so it
          closes with its own animation rather than vanishing. */}
      {releaseNotesArmed && (
        <ChunkErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <ReleaseNotesDialog
              open={showReleaseNotes}
              onOpenChange={setShowReleaseNotes}
              initialVersion={selectedVersion}
            />
          </Suspense>
        </ChunkErrorBoundary>
      )}

      {/* Units, offers, quotes and contacts opened in place from anywhere
          (openRecord, G12). Lazy inside; costs the shell almost nothing. */}
      <ChunkErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <RecordDialogHost />
        </Suspense>
      </ChunkErrorBoundary>

      {/* Contact Detail Modal. Same reasoning as the chat above: lazy, and with
          its own boundary because this sits outside <main>. */}
      {selectedContact && (
        <ChunkErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <ContactDetailModal
              isOpen={isContactModalOpen}
              onClose={() => {
                setIsContactModalOpen(false);
                setSelectedContact(null);
              }}
              contact={selectedContact}
              // Saving refreshes; closing stays the reader's action.
              onSave={() => {
                queryClient.invalidateQueries({ queryKey: ['/api/contacts', projectId] });
              }}
            />
          </Suspense>
        </ChunkErrorBoundary>
      )}
    </>
  );
}